import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Spinner, Table } from "reactstrap";

import ShellCard, { TableSurface } from "../../../../components/ShellCard";
import TablePagination from "../../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import { formatDocument, digitsOnly } from "../../../../utils/formatDoc";
import { paginate } from "../../../../utils/tableHelpers";

// Transportadoras do embarcador com o estado do acesso SFTP de cada uma (PO1-3186).
//
// A primeira versão (PO1-2167) listava só os acessos JÁ criados, porque o problema
// daquele momento era achar um acesso sem saber o CNPJ de cor. Só que, num embarcador
// que ainda não provisionou ninguém, a lista nascia vazia e sobrava o campo de CNPJ —
// exatamente o caso do piloto da Sinto (3 transportadoras vinculadas, 0 acessos, 22/09/2026).
// Agora a lista parte da CARTEIRA: quem está vinculado aparece mesmo sem acesso, e a
// linha oferece "Criar acesso" já com o CNPJ dela.
//
// Clicar na linha (ou no botão) cai no mesmo fluxo de sempre: preview → confirmar, e o
// card do acesso com "Mostrar senha". A lista nunca traz senha: o backend devolve só
// `temSenha`.

const PAGE_SIZE_INICIAL = 10;

const muted = { color: AUTOPILOT_COLORS.muted };

const TONES = {
  success: [AUTOPILOT_COLORS.statusSuccessBg, AUTOPILOT_COLORS.statusSuccess, AUTOPILOT_COLORS.statusSuccessBorder],
  warning: [AUTOPILOT_COLORS.statusWarningBg, AUTOPILOT_COLORS.statusWarning, AUTOPILOT_COLORS.statusWarningBorder],
  danger: [AUTOPILOT_COLORS.statusDangerBg, AUTOPILOT_COLORS.statusDanger, AUTOPILOT_COLORS.statusDangerBorder],
  neutral: [AUTOPILOT_COLORS.insetSurface, AUTOPILOT_COLORS.muted, AUTOPILOT_COLORS.borderSoft],
};

const pill = (tone) => {
  const [background, color, borda] = TONES[tone] || TONES.neutral;
  return {
    display: "inline-block",
    padding: "0.1rem 0.5rem",
    borderRadius: 999,
    fontSize: "0.72rem",
    fontWeight: 700,
    background,
    color,
    border: `1px solid ${borda}`,
  };
};

const ESTADOS = {
  sem_acesso: { rotulo: "Sem acesso", tone: "neutral" },
  gerenciado: { rotulo: "Gerenciado", tone: "success" },
  legado: { rotulo: "Legado", tone: "warning" },
  pendente: { rotulo: "Em andamento", tone: "warning" },
  revogado: { rotulo: "Revogado", tone: "danger" },
};

// Sem acento, sem pontuação e em minúsculas, para "Nepomuceno" achar "NEPOMUCENO -
// Filial (Betim-MG)" e o CNPJ achar tanto digitado quanto formatado.
function normalizar(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export default function TransportadorasDoEmbarcador({ itens, legadoConferido, host, porta, loading, onSelecionar, selecionadoId }) {
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_INICIAL);

  const filtrados = useMemo(() => {
    const lista = itens || [];
    const termo = normalizar(busca).trim();
    if (!termo) return lista;
    const digitos = digitsOnly(busca);
    return lista.filter((i) => {
      const t = i.transportadora || {};
      if (normalizar(t.nome).indexOf(termo) >= 0) return true;
      if (normalizar(i.usuario).indexOf(termo) >= 0) return true;
      if (digitos && String(t.cnpj || "").indexOf(digitos) >= 0) return true;
      return false;
    });
  }, [itens, busca]);

  // Buscar ou trocar de embarcador volta para a primeira página: senão a página 7 de
  // uma carteira grande fica apontando para o vazio depois do filtro.
  useEffect(() => {
    setPage(1);
  }, [busca, itens]);

  const total = (itens || []).length;
  const comAcesso = (itens || []).filter((i) => i.temAcesso).length;
  const pagina = paginate(filtrados, page, pageSize);

  const titulo = total
    ? `${total} transportadora(s) · ${comAcesso} com acesso SFTP`
    : "Nenhuma transportadora vinculada";

  return (
    <ShellCard eyebrow="Transportadoras deste embarcador" title={titulo} style={{ marginTop: "1rem" }}>
      {loading ? (
        <div style={{ ...muted, fontSize: "0.86rem" }}>
          <Spinner size="sm" /> Carregando…
        </div>
      ) : null}

      {!loading && total === 0 ? (
        <div style={{ ...muted, fontSize: "0.86rem" }}>
          Este embarcador não tem transportadora na carteira nem acesso SFTP criado. Informe o CNPJ
          abaixo para criar o primeiro.
        </div>
      ) : null}

      {!loading && total > 0 ? (
        <>
          {/* O backend confere no Wing quem já tem usuário antigo (legado sem registro).
              Se o Wing não respondeu, "Sem acesso" pode ser um legado não conferido. */}
          {legadoConferido === false ? (
            <div style={{ ...muted, fontSize: "0.8rem", marginBottom: "0.5rem" }}>
              Não foi possível consultar o Wing FTP agora: transportadoras com acesso antigo podem
              aparecer como “Sem acesso”. Ao abrir a linha, o sistema confere de novo e não cria
              usuário em cima de um existente.
            </div>
          ) : null}
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome, usuário ou CNPJ"
            style={{ maxWidth: 320, marginBottom: "0.75rem" }}
            aria-label="Buscar transportadora"
          />

          {filtrados.length === 0 ? (
            <div style={{ ...muted, fontSize: "0.86rem" }}>Nada encontrado para “{busca}”.</div>
          ) : (
            <TableSurface>
              <Table responsive className="hub-thead-accent" style={{ marginBottom: 0 }}>
                <thead className="text-primary">
                  <tr>
                    <th>Transportadora</th>
                    <th>Acesso SFTP</th>
                    <th>Usuário</th>
                    <th>Senha</th>
                    <th className="text-right">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {pagina.items.map((i) => {
                    const t = i.transportadora || {};
                    const e = ESTADOS[i.estado] || { rotulo: i.estado, tone: "warning" };
                    const ativo = String(selecionadoId) === String(i.idTransportadora);
                    const abrir = () => onSelecionar && onSelecionar(i);
                    return (
                      <tr
                        key={i.idTransportadora}
                        onClick={abrir}
                        style={{
                          cursor: "pointer",
                          background: ativo ? AUTOPILOT_COLORS.insetSurfaceActive : undefined,
                        }}
                      >
                        <td>
                          <div style={{ color: AUTOPILOT_COLORS.text }}>{t.nome || "—"}</div>
                          <div style={{ ...muted, fontSize: "0.76rem" }}>{formatDocument(t.cnpj || "")}</div>
                        </td>
                        <td>
                          <span style={pill(e.tone)}>{e.rotulo}</span>
                          {i.operacaoEmAndamento ? (
                            <div style={{ ...muted, fontSize: "0.74rem", marginTop: "0.2rem" }}>
                              operação em andamento
                            </div>
                          ) : null}
                          {i.temAcesso && i.naCarteira === false ? (
                            <div style={{ ...muted, fontSize: "0.74rem", marginTop: "0.2rem" }}>
                              fora da carteira
                            </div>
                          ) : null}
                        </td>
                        <td style={{ wordBreak: "break-all" }}>{i.usuario || <span style={muted}>—</span>}</td>
                        <td style={muted}>
                          {!i.temAcesso
                            ? "—"
                            : i.legadoSemRegistro
                            ? "definida fora do Hub"
                            : i.modo === "ssh"
                            ? `${i.chaves} chave(s) SSH`
                            : i.temSenha
                            ? "guardada"
                            : "não guardada"}
                        </td>
                        <td className="text-right">
                          <Button
                            size="sm"
                            color={i.temAcesso ? "info" : "primary"}
                            outline={Boolean(i.temAcesso)}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              abrir();
                            }}
                          >
                            {i.temAcesso ? "Abrir" : "Criar acesso"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <TablePagination
                page={pagina.page}
                pageSize={pageSize}
                total={filtrados.length}
                itemLabel="transportadora(s)"
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </TableSurface>
          )}

          <div style={{ ...muted, fontSize: "0.8rem", marginTop: "0.6rem" }}>
            {host ? `Host ${host}, porta ${porta}. ` : null}A senha só aparece no card do acesso, e
            cada exibição fica registrada.
          </div>
        </>
      ) : null}
    </ShellCard>
  );
}
